import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTimModalStore } from './useTimModalStore';

const HISTORY_KEY = '__ahaTimModal';
const getEntry = (state: unknown): string | null => {
  if (!state || typeof state !== 'object') return null;
  const id = (state as Record<string, unknown>)[HISTORY_KEY];
  return typeof id === 'string' && id ? id : null;
};

/** Give DM its own history entry while preserving the host URL and route state. */
export function useTimModalHistory() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showTimModal, setShowTimModal, resumeTimModal } = useTimModalStore();
  const activeEntry = useRef<string | null>(null);
  const closing = useRef(false);
  const initialized = useRef(false);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const wasClosing = closing.current;
      closing.current = false;
      const id = getEntry(event.state);
      if (id) {
        activeEntry.current = id;
        // The resident iframe owns its selected conversation. Do not replay OPEN.
        if (!wasClosing) resumeTimModal();
      } else if (activeEntry.current) {
        activeEntry.current = null;
        if (!wasClosing) setShowTimModal(false);
      }
    };

    // Router POP listeners may commit synchronously; reconcile visibility first.
    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, [setShowTimModal, resumeTimModal]);

  useEffect(() => {
    const initialMount = !initialized.current;
    initialized.current = true;
    // A POP can restore the store while effects from an older render are pending.
    if (
      closing.current ||
      showTimModal !== useTimModalStore.getState().showTimModal
    )
      return;
    const currentEntry = getEntry(window.history.state);
    if (!showTimModal) {
      // A refresh loses refs, but its same-page DM entry is still in history.
      // Later closed route renders may belong to a Forward restoration in progress.
      const id = activeEntry.current || (initialMount ? currentEntry : null);
      activeEntry.current = null;
      if (id && currentEntry === id) {
        closing.current = true;
        navigate(-1);
      }
      return;
    }

    // Let the host consume DM email/deep-link parameters before adding an entry.
    if (new URLSearchParams(location.search).get('tim') === '1') return;

    if (activeEntry.current && currentEntry !== activeEntry.current) {
      activeEntry.current = null;
      setShowTimModal(false);
      return;
    }

    // Reuse a surviving entry when the open modal is mounted again.
    if (currentEntry) {
      activeEntry.current = currentEntry;
      return;
    }

    if (!activeEntry.current) {
      const id = `tim-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      activeEntry.current = id;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash
        },
        { state: location.state, preventScrollReset: true }
      );
      // Keep the marker outside React Router's user state, which may be any value.
      window.history.replaceState(
        { ...window.history.state, [HISTORY_KEY]: id },
        ''
      );
    }
  }, [showTimModal, location, navigate, setShowTimModal]);
}
